import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../axiosConfig";
import Swal from "sweetalert2";

export default function StaffAttendance({ user, department }) {
  const [busy, setBusy] = useState(false);
  const [present, setPresent] = useState(null);
  const submitting = useRef(false);

  useEffect(() => {
    let active = true;
    setPresent(null);
    if (user?.EmployeeID) {
      axiosInstance.get("/api/presentEmployees").then(({ data }) => {
        if (active) setPresent(data.some(row => String(row.EmployeeID) === String(user.EmployeeID)));
      }).catch(() => {
        if (active) Swal.fire({ icon: "warning", title: "Attendance status unavailable", text: "You can retry using Punch In or Punch Out." });
      });
    }
    return () => { active = false; };
  }, [user?.EmployeeID]);

  const punch = async (direction) => {
    if (submitting.current || !user?.EmployeeID) return;
    submitting.current = true;
    setBusy(true);
    try {
      const { data } = await axiosInstance.get("/api/presentEmployees");
      const isPresent = data.some(row => String(row.EmployeeID) === String(user.EmployeeID));
      setPresent(isPresent);
      if (direction === "in") {
        if (isPresent) throw new Error("You are already punched in.");
        const assigned = [department, user.Department].find(value => value?.trim() && value.trim().toLowerCase() !== "not assigned");
        if (!assigned) throw new Error("Department is compulsory. Please contact your admin to assign your department.");
        const position = await new Promise((resolve, reject) => {
          if (!navigator.geolocation) return reject(new Error("Geolocation is not supported by your browser."));
          navigator.geolocation.getCurrentPosition(resolve,
            () => reject(new Error("Please enable Location/GPS permission and try again.")),
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
        });
        await axiosInstance.post("/api/mark-in", {
          EmployeeID: user.EmployeeID,
          Department: assigned.trim(),
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      } else {
        if (!isPresent) throw new Error("You are not punched in.");
        const confirmation = await Swal.fire({
          icon: "question",
          title: "Punch Out?",
          text: "Are you sure you want to punch out?",
          showCancelButton: true,
          confirmButtonText: "Yes, Punch Out",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#dc3545",
          focusCancel: true
        });
        if (!confirmation.isConfirmed) return;
        await axiosInstance.post("/api/mark-out");
      }
      setPresent(direction === "in");
      await Swal.fire({ icon: "success", title: direction === "in" ? "Punched In!" : "Punched Out!", text: `Punched ${direction} successfully!` });
    } catch (error) {
      await Swal.fire({ icon: "error", title: "Attendance could not be recorded", text: error.response?.data?.error || error.message || "Unable to record attendance. Please try again." });
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return <section className="card p-3 mb-3" aria-label="Staff attendance">
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
      <div><h5 className="mb-1">Attendance</h5><span className="text-muted" role="status">{present === null ? "Status unavailable" : present ? "Punched in" : "Punched out"}</span></div>
      <div className="d-flex gap-2">
        <button type="button" className="btn btn-success" disabled={busy || !user?.EmployeeID} onClick={() => punch("in")}>Punch In</button>
        <button type="button" className="btn btn-danger" disabled={busy || !user?.EmployeeID} onClick={() => punch("out")}>Punch Out</button>
      </div>
    </div>
    {busy && <div className="small mt-2" role="status">Recording attendance…</div>}
  </section>;
}
